import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Package, Ticket, ShoppingBag, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalTemplates: 0,
    visibleTemplates: 0,
    totalCoupons: 0,
    availableCoupons: 0,
    totalOrders: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    const isAuth = localStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const [templatesRes, couponsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/templates`),
        axios.get(`${API}/admin/coupons`),
        axios.get(`${API}/admin/orders`)
      ]);

      const templates = templatesRes.data;
      const coupons = couponsRes.data.coupons;
      const orders = ordersRes.data;

      setStats({
        totalTemplates: templates.length,
        visibleTemplates: templates.filter(t => t.is_visible).length,
        totalCoupons: coupons.length,
        availableCoupons: coupons.filter(c => c.status === 'available').length,
        totalOrders: orders.length,
        totalRevenue: orders.reduce((sum, o) => sum + (o.amount || 0), 0)
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <AdminLayout>
      <div>
        <h1 className="text-3xl font-bold text-black mb-8">Dashboard</h1>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-[#E3F2FD] rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-[#1E88E5]" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">Templates</h3>
            <p className="text-3xl font-bold text-black">{stats.visibleTemplates}/{stats.totalTemplates}</p>
            <p className="text-xs text-gray-500 mt-1">Visible / Total</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Ticket className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">Coupons Available</h3>
            <p className="text-3xl font-bold text-black">{stats.availableCoupons}/{stats.totalCoupons}</p>
            <p className="text-xs text-gray-500 mt-1">Available / Total</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">Total Orders</h3>
            <p className="text-3xl font-bold text-black">{stats.totalOrders}</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">Total Revenue</h3>
            <p className="text-3xl font-bold text-black">${stats.totalRevenue.toFixed(2)}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-black mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/admin/templates')}
                className="w-full px-4 py-3 bg-[#E3F2FD] text-[#1E88E5] rounded-lg hover:bg-[#1E88E5] hover:text-white transition-colors font-medium text-left"
              >
                Manage Templates
              </button>
              <button
                onClick={() => navigate('/admin/coupons')}
                className="w-full px-4 py-3 bg-[#E3F2FD] text-[#1E88E5] rounded-lg hover:bg-[#1E88E5] hover:text-white transition-colors font-medium text-left"
              >
                Upload Coupons
              </button>
              <button
                onClick={() => navigate('/admin/orders')}
                className="w-full px-4 py-3 bg-[#E3F2FD] text-[#1E88E5] rounded-lg hover:bg-[#1E88E5] hover:text-white transition-colors font-medium text-left"
              >
                View Orders
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-black mb-4">System Info</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Platform Status</span>
                <span className="text-green-600 font-semibold">Active</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Payment Gateway</span>
                <span className="text-black font-semibold">Stripe (Test Mode)</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Email Service</span>
                <span className="text-black font-semibold">Hostinger SMTP</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Scheduled Emails</span>
                <span className="text-black font-semibold">Active (5 min check)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;